import { useContext } from "react";
import { Navigate, useParams } from "react-router-dom";
import { DEFAULT_LAYOUT_SLUG, LAYOUT_SLUGS } from "../constants/layout";
import { LOAN_APPLICATION_ROUTE_SEGMENTS } from "../Components/Credora/Loans/loansSectionPaths";
import {
  ExecutiveDashboard,
  KeyMetricsDashboard,
  TodayActivityDashboard,
  CollectionsDashboard,
  ComplianceDashboard,
  CommunicationsDashboard,
  CallCenterDashboard,
  DigitalLendingReport,
  RevenueProfitabilityPage,
  WorkingCapitalReportPage,
  FundingAccountsReportPage,
  CallCenterHub,
  TicketDetail,
  ComplianceOperationsHub,
  ControlRegistryPage,
  KycVerificationPage,
  PendingKycPage,
  VerifiedKycPage,
  RejectedKycPage,
  BorrowersList,
  BorrowerDetail,
  CreditHistoryPage,
  GuarantorsPage,
  CollateralPage,
  BorrowersSegmentRoute,
  LoansList,
  LoanDetail,
  NewLoanApplicationPage,
  LoanGroupsPage,
  EmiSchedulePage,
  RestructuredLoansPage,
  CollectionsList,
  CollectionCaseDetail,
  RiskAlertsPage,
  PtpPage,
  WriteOffsPage,
  CommunicationsHub,
  CredoraComingSoon,
  AiCreditScoringPage,
  AiRiskAlertsPage,
  AiChurnPredictionPage,
  AiRecommendationsPage,
  DisbursementsPage,
  PayoutsPage,
  FeesPage,
  WorkingCapitalPage,
  FundingAccountsPage,
  ProductAllocationsPage,
  CapitalLedgerPage,
  SettingsOverviewPage,
  FeeConfigurationPage,
  LoanProductsSettingsPage,
  BranchesSettingsPage,
  CommunicationSettingsPage,
  DisbursementSettingsPage,
  TreasurySettingsPage,
  CollectionSettingsPage,
  AiSettingsPage,
  RiskAlertSettingsPage,
  GuarantorSettingsPage,
  KycSettingsPage,
  SecuritySettingsPage,
  SettingsPlaceholderPage,
  RepaymentCalendar,
  OverdueRepaymentsPage,
  UpcomingEmisPage,
  CollectedTodayPage,
  UsersList,
  RolesPermissions,
  RolesSettingsPage,
  DepartmentsSettingsPage,
  TeamsView,
  DocumentationPage,
  AccountPage,
  SmsGatewaysPage,
  SmtpGatewaysPage,
  WhatsappWebhooksPage,
  ScoringApisPage,
  FinanceReportsExportPage,
} from "./credoraLazyPages";
import { creditHistoryPath, collateralPath, guarantorsPath } from "../Components/Credora/Borrowers/borrowerPaths";
import CustomizerContext from "../_helper/Customizer";
import CredoraRedirect from "./CredoraRedirect";
import RepaymentsFailedRedirect from "./RepaymentsFailedRedirect";
import { buildAccountingRoutes } from "./accountingRoutes";
import { buildFinanceReportRoutes } from "./financeReportsRoutes";
import { ACCOUNTING_PLACEHOLDERS } from "../Components/Credora/Accounting/accountingPlaceholders";

const pub = process.env.PUBLIC_URL || "";
const p = (segment) => `${pub}${segment}`;

function LegacyBorrowerViewRedirect() {
  const { id } = useParams();
  return <Navigate to={p(`/credora/borrowers/${id}`)} replace />;
}

function LegacyComplianceBorrowerRedirect() {
  const { id } = useParams();
  return <Navigate to={p(`/credora/borrowers/${id}`)} replace />;
}

function CreditHistoryLayoutRedirect() {
  const { layoutURL } = useContext(CustomizerContext);
  return <Navigate to={creditHistoryPath(pub, layoutURL)} replace />;
}

function GuarantorsLayoutRedirect() {
  const { layoutURL } = useContext(CustomizerContext);
  return <Navigate to={guarantorsPath(pub, layoutURL)} replace />;
}

function CollateralLayoutRedirect() {
  const { layoutURL } = useContext(CustomizerContext);
  return <Navigate to={collateralPath(pub, layoutURL)} replace />;
}

function LegacySmsGatewaysRedirect() {
  const { layout } = useParams();
  const { layoutURL } = useContext(CustomizerContext);
  const slug = layout || layoutURL;
  return <Navigate to={p(`/credora/integrations/sms-gateways/${slug}`)} replace />;
}

/** Legacy Active Loans URLs under /credora/loans/* → top-level routes. */
function legacyActiveLoansRedirect(targetPath) {
  function LegacyActiveLoansRedirect() {
    const { layout } = useParams();
    const { layoutURL } = useContext(CustomizerContext);
    const slug = layout || layoutURL;
    return <Navigate to={p(`${targetPath}/${slug}`)} replace />;
  }
  return <LegacyActiveLoansRedirect />;
}

const legacyLoansActiveRedirect = legacyActiveLoansRedirect("/credora/active-loan");
const legacyLoansScheduleRedirect = legacyActiveLoansRedirect("/credora/schedule-emi");
const legacyLoansOverdueRedirect = legacyActiveLoansRedirect("/credora/overdue-loans");
const legacyLoansRestructuredRedirect = legacyActiveLoansRedirect("/credora/restructured-loans");

/** /credora/loans/:layout — layout is theme slug, or a loan segment (drafts, pending, …) to redirect. */
function LoansApplicationsLayoutRoute() {
  const { layout } = useParams();
  const { layoutURL } = useContext(CustomizerContext);

  if (layout && LOAN_APPLICATION_ROUTE_SEGMENTS.has(layout)) {
    const slug = LAYOUT_SLUGS.includes(layoutURL) ? layoutURL : DEFAULT_LAYOUT_SLUG;
    return <Navigate to={p(`/credora/loans/${layout}/${slug}`)} replace />;
  }

  return (
    <LoansList
      mainTitle="Loan Applications"
      pageTitle="All Applications"
      navActiveId="all"
      routeKey="all"
    />
  );
}

const soon = (props) => <CredoraComingSoon {...props} />;

/** LoanProX-aligned routes (BNPL / Merchants excluded). */
export const credoraAppRoutes = [
  { path: p("/credora/dashboard/overview/:layout"), element: <ExecutiveDashboard /> },
  { path: p("/credora/dashboard/key-metrics/:layout"), element: <KeyMetricsDashboard /> },
  { path: p("/credora/dashboard/today-activity/:layout"), element: <TodayActivityDashboard /> },
  { path: p("/credora/dashboard/executive/:layout"), element: <ExecutiveDashboard /> },
  { path: p("/credora/dashboard/loan-officer/:layout"), element: <KeyMetricsDashboard /> },
  { path: p("/credora/dashboard/collections/:layout"), element: <CollectionsDashboard /> },
  { path: p("/credora/dashboard/compliance/:layout"), element: <ComplianceDashboard /> },
  { path: p("/credora/dashboard/communications/:layout"), element: <CommunicationsDashboard /> },
  { path: p("/credora/dashboard/call-center/:layout"), element: <CallCenterDashboard /> },

  { path: p("/credora/ai/credit-scoring/:layout"), element: <AiCreditScoringPage /> },
  { path: p("/credora/ai/risk-alerts/:layout"), element: <AiRiskAlertsPage /> },
  { path: p("/credora/ai/churn-prediction/:layout"), element: <AiChurnPredictionPage /> },
  { path: p("/credora/ai/recommendations/:layout"), element: <AiRecommendationsPage /> },

  { path: p("/credora/loans/view/:id/:layout"), element: <LoanDetail /> },
  { path: p("/credora/loans/new/group/:layout"), element: <NewLoanApplicationPage initialTypeTab="group" /> },
  { path: p("/credora/loans/new/:layout"), element: <NewLoanApplicationPage /> },
  { path: p("/credora/loans/group-registry/:layout"), element: <LoanGroupsPage /> },
  {
    path: p("/credora/loans/groups/:layout"),
    element: (
      <LoansList
        mainTitle="Loan Applications"
        pageTitle="Group Applications"
        subtitle="Loan applications linked to registered VSLA and group lending cohorts"
        navActiveId="groups"
        routeKey="groups"
        showNewButton
        emptyMessage="No group loan applications."
      />
    ),
  },
  {
    path: p("/credora/loans/drafts/:layout"),
    element: (
      <LoansList
        mainTitle="Loan Applications"
        pageTitle="Draft Applications"
        subtitle="Saved applications not yet submitted for review"
        navActiveId="drafts"
        routeKey="drafts"
        showNewButton
        emptyMessage="No draft applications."
      />
    ),
  },
  {
    path: p("/credora/loans/pending/:layout"),
    element: (
      <LoansList
        mainTitle="Loan Applications"
        pageTitle="Pending Applications"
        navActiveId="pending"
        routeKey="pending"
        showNewButton={false}
        emptyMessage="No pending applications."
      />
    ),
  },
  {
    path: p("/credora/loans/approved/:layout"),
    element: (
      <LoansList
        mainTitle="Loan Applications"
        pageTitle="Approved"
        navActiveId="approved"
        routeKey="approved"
        showNewButton={false}
        emptyMessage="No approved applications."
      />
    ),
  },
  {
    path: p("/credora/loans/rejected/:layout"),
    element: (
      <LoansList
        mainTitle="Loan Applications"
        pageTitle="Rejected"
        navActiveId="rejected"
        routeKey="rejected"
        showNewButton={false}
        emptyMessage="No rejected applications."
      />
    ),
  },
  {
    path: p("/credora/active-loan/:layout"),
    element: (
      <LoansList
        mainTitle="Active Loans"
        pageTitle="All Active Loans"
        routeKey="active"
        activeLoansNavId="active"
        showNewButton={false}
        showAiBanner={false}
        subtitle="Live disbursed loans in the portfolio"
        emptyMessage="No active loans."
      />
    ),
  },
  {
    path: p("/credora/schedule-emi/:layout"),
    element: <EmiSchedulePage />,
  },
  {
    path: p("/credora/overdue-loans/:layout"),
    element: (
      <LoansList
        mainTitle="Active Loans"
        pageTitle="Overdue Loans"
        routeKey="overdue"
        activeLoansNavId="overdue"
        showNewButton={false}
        showAiBanner={false}
        subtitle="Active loans with past-due installments and outstanding arrears"
        emptyMessage="No overdue loans — all active loans are current on repayments."
      />
    ),
  },
  {
    path: p("/credora/restructured-loans/:layout"),
    element: <RestructuredLoansPage />,
  },
  { path: p("/credora/loans/active/:layout"), element: legacyLoansActiveRedirect },
  { path: p("/credora/loans/schedule/:layout"), element: legacyLoansScheduleRedirect },
  { path: p("/credora/loans/overdue/:layout"), element: legacyLoansOverdueRedirect },
  { path: p("/credora/loans/restructured/:layout"), element: legacyLoansRestructuredRedirect },
  {
    path: p("/credora/loans/:layout"),
    element: <LoansApplicationsLayoutRoute />,
  },

  {
    path: p("/credora/repayments/calendar/:layout"),
    element: <RepaymentCalendar />,
  },
  {
    path: p("/credora/repayments/upcoming/:layout"),
    element: <UpcomingEmisPage />,
  },
  {
    path: p("/credora/repayments/collected/:layout"),
    element: <CollectedTodayPage />,
  },
  {
    path: p("/credora/repayments/failed"),
    element: <CredoraRedirect basePath={p("/credora/repayments/overdue")} />,
  },
  {
    path: p("/credora/repayments/overdue/:layout"),
    element: <OverdueRepaymentsPage />,
  },
  {
    path: p("/credora/repayments/failed/:layout"),
    element: <RepaymentsFailedRedirect />,
  },

  { path: p("/credora/collections/view/:id/:layout"), element: <CollectionCaseDetail /> },
  { path: p("/credora/collections/:layout"), element: <CollectionsList /> },
  {
    path: p("/credora/collections/alerts/:layout"),
    element: <RiskAlertsPage />,
  },
  {
    path: p("/credora/collections/ptp/:layout"),
    element: <PtpPage />,
  },
  {
    path: p("/credora/collections/write-offs/:layout"),
    element: <WriteOffsPage />,
  },

  {
    path: p("/credora/borrowers/credit-history"),
    element: <CreditHistoryLayoutRedirect />,
  },
  {
    path: p("/credora/borrowers/credit-history/:layout"),
    element: <CreditHistoryPage />,
  },
  {
    path: p("/credora/borrowers/guarantors"),
    element: <GuarantorsLayoutRedirect />,
  },
  {
    path: p("/credora/borrowers/guarantors/:layout"),
    element: <GuarantorsPage />,
  },
  {
    path: p("/credora/borrowers/collateral"),
    element: <CollateralLayoutRedirect />,
  },
  {
    path: p("/credora/borrowers/collateral/:layout"),
    element: <CollateralPage />,
  },
  { path: p("/credora/borrowers/:segment"), element: <BorrowersSegmentRoute /> },
  { path: p("/credora/compliance/borrowers/:id/:layout"), element: <LegacyComplianceBorrowerRedirect /> },
  { path: p("/credora/borrowers/view/:id/:layout"), element: <LegacyBorrowerViewRedirect /> },
  {
    path: p("/credora/compliance/kyc/pending/:layout"),
    element: <PendingKycPage />,
  },
  {
    path: p("/credora/compliance/kyc/verified/:layout"),
    element: <VerifiedKycPage />,
  },
  {
    path: p("/credora/compliance/kyc/rejected/:layout"),
    element: <RejectedKycPage />,
  },

  { path: p("/credora/communication/:layout"), element: <CommunicationsHub /> },
  { path: p("/credora/communications/:layout"), element: <CommunicationsHub /> },

  {
    path: p("/credora/finance/payouts/:layout"),
    element: <PayoutsPage />,
  },
  {
    path: p("/credora/finance/disbursements/:layout"),
    element: <DisbursementsPage />,
  },
  {
    path: p("/credora/finance/fees/:layout"),
    element: <FeesPage />,
  },
  {
    path: p("/credora/finance/working-capital/:layout"),
    element: <WorkingCapitalPage />,
  },
  {
    path: p("/credora/finance/funding-accounts/:layout"),
    element: <FundingAccountsPage />,
  },
  {
    path: p("/credora/finance/product-allocations/:layout"),
    element: <ProductAllocationsPage />,
  },
  {
    path: p("/credora/finance/capital-ledger/:layout"),
    element: <CapitalLedgerPage />,
  },

  ...ACCOUNTING_PLACEHOLDERS.map((page) => ({
    path: p(`/credora/${page.path}`),
    element: <CredoraRedirect basePath={`/credora/${page.path}`} />,
  })),
  ...buildAccountingRoutes(p),
  ...buildFinanceReportRoutes(p),

  { path: p("/credora/settings/fee-configuration/:layout"), element: <FeeConfigurationPage /> },
  { path: p("/credora/settings/loan-products/:layout"), element: <LoanProductsSettingsPage /> },
  { path: p("/credora/settings/branches/:layout"), element: <BranchesSettingsPage /> },
  { path: p("/credora/settings/roles/:layout"), element: <RolesSettingsPage /> },
  { path: p("/credora/settings/departments/:layout"), element: <DepartmentsSettingsPage /> },
  { path: p("/credora/settings/communications/:layout"), element: <CommunicationSettingsPage /> },
  { path: p("/credora/settings/disbursement/:layout"), element: <DisbursementSettingsPage /> },
  { path: p("/credora/settings/treasury/:layout"), element: <TreasurySettingsPage /> },
  { path: p("/credora/settings/collections/:layout"), element: <CollectionSettingsPage /> },
  { path: p("/credora/settings/risk-alerts/:layout"), element: <RiskAlertSettingsPage /> },
  { path: p("/credora/settings/kyc/:layout"), element: <KycSettingsPage /> },
  { path: p("/credora/settings/guarantors/:layout"), element: <GuarantorSettingsPage /> },
  { path: p("/credora/settings/ai/:layout"), element: <AiSettingsPage /> },
  { path: p("/credora/settings/security/:layout"), element: <SecuritySettingsPage /> },
  { path: p("/credora/settings/:layout"), element: <SettingsOverviewPage /> },

  {
    path: p("/credora/reports/portfolio/:layout"),
    element: <ExecutiveDashboard />,
  },
  {
    path: p("/credora/reports/emi-breakdown/:layout"),
    element: <DigitalLendingReport />,
  },
  {
    path: p("/credora/reports/aging/:layout"),
    element: <CollectionsDashboard />,
  },
  {
    path: p("/credora/reports/revenue/:layout"),
    element: <RevenueProfitabilityPage />,
  },
  {
    path: p("/credora/reports/working-capital/:layout"),
    element: <WorkingCapitalReportPage />,
  },
  {
    path: p("/credora/reports/funding-accounts/:layout"),
    element: <FundingAccountsReportPage />,
  },
  { path: p("/credora/reports/digital-lending/:layout"), element: <DigitalLendingReport /> },
  {
    path: p("/credora/reports/export/:layout"),
    element: <FinanceReportsExportPage />,
  },

  { path: p("/credora/integrations/sms-gateways"), element: <CredoraRedirect basePath="/credora/integrations/sms-gateways" /> },
  { path: p("/credora/integrations/smsgetways"), element: <CredoraRedirect basePath="/credora/integrations/smsgetways" /> },
  { path: p("/credora/integrations/smsgetways/:layout"), element: <LegacySmsGatewaysRedirect /> },
  { path: p("/credora/integrations/sms-gateways/:layout"), element: <SmsGatewaysPage /> },
  { path: p("/credora/integrations/smtp-gateways"), element: <CredoraRedirect basePath="/credora/integrations/smtp-gateways" /> },
  { path: p("/credora/integrations/smtp-gateways/:layout"), element: <SmtpGatewaysPage /> },
  { path: p("/credora/integrations/whatsapp-webhooks"), element: <CredoraRedirect basePath="/credora/integrations/whatsapp-webhooks" /> },
  { path: p("/credora/integrations/whatsapp-webhooks/:layout"), element: <WhatsappWebhooksPage /> },
  {
    path: p("/credora/integrations/payments/:layout"),
    element: soon({ mainTitle: "Integrations & API", title: "Payment Gateways" }),
  },
  { path: p("/credora/integrations/scoring"), element: <CredoraRedirect basePath="/credora/integrations/scoring" /> },
  { path: p("/credora/integrations/scoring/:layout"), element: <ScoringApisPage /> },
  {
    path: p("/credora/integrations/webhooks/:layout"),
    element: soon({ mainTitle: "Integrations & API", title: "Webhooks" }),
  },

  { path: p("/credora/users/roles/:layout"), element: <RolesPermissions /> },
  { path: p("/credora/users/teams/:layout"), element: <TeamsView /> },
  { path: p("/credora/users/:layout"), element: <UsersList /> },

  { path: p("/credora/call-center"), element: <CredoraRedirect basePath="/credora/call-center" /> },
  { path: p("/credora/dashboard/call-center"), element: <CredoraRedirect basePath="/credora/dashboard/call-center" /> },
  { path: p("/credora/call-center/tickets/view/:id/:layout"), element: <TicketDetail /> },
  { path: p("/credora/call-center/:layout"), element: <CallCenterHub /> },
  {
    path: p("/credora/support/help/:layout"),
    element: soon({ mainTitle: "Support & Help", title: "Help Center" }),
  },
  { path: p("/credora/support/docs"), element: <CredoraRedirect basePath="/credora/support/docs" /> },
  { path: p("/credora/support/docs/:layout"), element: <DocumentationPage /> },
  { path: p("/credora/account/:layout"), element: <AccountPage /> },
  {
    path: p("/credora/support/contact/:layout"),
    element: <CallCenterHub />,
  },

  { path: p("/credora/compliance/control-registry/:layout"), element: <ControlRegistryPage /> },
  { path: p("/credora/compliance/operations/:layout"), element: <ComplianceOperationsHub /> },
  { path: p("/credora/compliance/audit-logs/:layout"), element: <ComplianceOperationsHub defaultTab="audit" /> },
  { path: p("/credora/compliance/kyc-verification/:layout"), element: <KycVerificationPage /> },
];
